/* eslint-disable import/order */
import 'react-app-polyfill/ie11'
import 'react-app-polyfill/stable'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/700.css'
import React, { Suspense } from 'react'
import ReactDOM from 'react-dom'
import { Provider } from 'react-redux'
import { I18nextProvider } from 'react-i18next'
import { toast } from 'react-toastify'
import { ApolloProvider } from '@apollo/client'
import { disableReactDevTools } from '@fvilers/disable-react-devtools'
import i18n from './i18n'
import * as serviceWorker from './serviceWorker'
import { store } from './store/store'
import { store as newStore } from './store'
import { client } from 'client'
import * as userActions from 'store/actions/user.action'
import { ConnectedRouter } from 'connected-react-router'
import { history } from './store/reducers'
import { Switch, Route, Redirect } from 'react-router'
import EditCourse from 'components/administration/components/courses/components/courses/edit/Edit'
import Hierarchy from 'components/configuration/components/hierarchy/Hierarchy'
import Users from 'components/configuration/components/users/Users'
import Get from 'components/courses/components/get/Get'
import NewCourse from 'components/administration/components/courses/components/courses/new/New'
import CoursesAdministration from 'components/administration/components/courses'
import TemplatesAdministration from 'components/administration/components/courses/templates'
import ExternalCoursesAdministration from 'components/administration/components/courses/externalCourses'
import LessonsIndex from 'components/administration/components/lessons/Index'
import BlockIndex from 'components/administration/components/blocks/Index'
import LessonView from 'components/courses/lessons/View'
import AdminCoursesWrapper from 'components/administration/view/View'
import Loader from 'components/ui/loader/Loader'
import UserMonitor from 'components/administration/components/monitor/components/user/Index'
import User from 'components/configuration/components/user/User'
import CoursePreview from 'components/administration/components/lessons/components/preview/Index'
import Evaluation from 'components/courses/lessons/components/LessonCard/components/ContentBlock/components/evaluation/Evaluation'
import NewHome from './components/new-home/NewHome'
import 'react-toastify/dist/ReactToastify.css'
import 'antd/dist/antd.css'
import 'scss/bootstrap.scss'
import 'scss/lms.scss'
import '@rankmi/rankmi-table/dist/index.css'
import Error404 from 'components/error-pages/error-404'
import Error403 from 'components/error-pages/error-403'
import Generate from './components/home/components/my-certificates/components/Generate'
import { ADMIN_ROUTE, PathRoutes, ProfileRoutes } from 'enum/routes'
import { Monitoring } from 'components/monitoring'
import ScormPage from 'components/scorm'
import { RankmiAmplitudeWrapper } from 'contexts'
import { EvaluationStatesEnum } from 'enum/evaluation-states.enum'
import { MainDataProvider } from 'components/monitoring/context'
import { CHAMELEON_KEY, isProd } from 'config'
import chmln from '@chamaeleonidae/chmln'
import { useChameleon } from 'hooks/useChameleon'
import { canActiveChmln } from 'helpers/chameleon.helper'
import { LmsProfilesPage } from 'pages/configuration/LmsProfiles'
import { AccessConsumer, AccessProvider } from 'contexts/AccessContext'
import IntegrationsConfig from 'components/configuration/components/integrations/IntegrationsConfig'
import MyCourses from 'components/home/components/my-courses'
import MyCertificates from 'components/home/components/my-certificates/MyCertificates'
import ExternalCourses from 'components/home/components/externalCourses'
import AdminApplications from 'components/home/components/admin-applications/AdminApplications'
import { Messages } from 'components/home/components/messages/Messages'
import { ViewAll } from 'components/home/components/view-all/ViewAll'
import Home from 'pages/Home'
import { Library } from 'pages/Library'
import { LearningPath } from 'pages/LearningPath'
import LearningPathsManage from 'components/administration/components/learningPaths/manage'
import UserProfile from 'components/configuration/components/userProfile'
import { Webinars } from 'components/webinars'
import { ReaderMessageProvider } from 'contexts/ReaderMessageContext'
import ExternalCourseAdministration from 'components/administration/components/courses/externalCourse'

const Requests = React.lazy(
  () => import('./components/home/components/request-integration/Request')
)

if (canActiveChmln()) {
  try {
    chmln?.init(CHAMELEON_KEY, {
      fastUrl: 'https://fast.chameleon.io/',
    })
  } catch (error) {
    console.error('Chameleon init failed', error)
  }
}

const ImportCenter = React.lazy(
  () => import('components/administration/components/ImportCenter')
)

const LearningPaths = React.lazy(
  () => import('components/administration/components/learningPaths')
)

const CertificatesGenerate = React.lazy(
  () => import('components/home/components/CertificatesGenerate')
)

const DownloadCertificate = React.lazy(
  () => import('components/home/components/DownloadCertificate')
)

const TranslationsConfig = React.lazy(
  () => import('components/configuration/components/translations/Translations')
)

const Reports = React.lazy(() => import('components/reports'))

toast.configure({
  className: 'toast-container',
  autoClose: 5000,
})

i18n.on('initialized', () => {
  console.log('i18n initialized')
})

store.dispatch({ type: userActions.DECODE_JWT })
// store.dispatch({ type: lmsProfileActions.LMS_PROFILES_LIST_GET, payload: { page: 1, searchText: "", sort: [] } });

const Contexts: React.FC = ({ children }) => (
  <ApolloProvider client={client}>
    <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
  </ApolloProvider>
)

const AppRoutes: React.FC = ({ children }) => {
  useChameleon()
  return (
    <AccessProvider>
      <ReaderMessageProvider>
        <Switch>
          <Route exact path={PathRoutes.SCORM} component={ScormPage} />{' '}
          <Route
            exact
            path={PathRoutes.COURSE_CERTIFICATES_GENERATE}
            component={CertificatesGenerate}
          />
          <Route
            exact
            path={PathRoutes.DOWNLOAD_CERTIFICATE}
            component={DownloadCertificate}
          />
          <Route path="*">{children}</Route>
        </Switch>
      </ReaderMessageProvider>
    </AccessProvider>
  )
}

const Layout = ({ children }) => {
  return <NewHome>{children}</NewHome>
}

if (isProd) disableReactDevTools()

ReactDOM.render(
  <Contexts>
    <RankmiAmplitudeWrapper>
      <Provider store={store}>
        <ConnectedRouter history={history}>
          <Suspense fallback={<Loader overlay color />}>
            <AppRoutes>
              <Layout>
                <Switch>
                  <Route path={PathRoutes.REPORTS}>
                    <Reports />
                  </Route>
                  <Route exact path="/" component={Home} />
                  <Route exact path="/library" component={Library} />
                  <Route
                    exact
                    path="/certificates"
                    component={() => {
                      return <MyCertificates title={''} />
                    }}
                  />
                  <Route
                    exact
                    path="/my-courses/:origin?"
                    component={MyCourses}
                  />

                  <Route exact path="/messages" component={Messages} />

                  {/* <Route exact path="/applications" component={Applications} /> */}
                  <Route
                    exact
                    path="/admin-applications"
                    component={AdminApplications}
                  />
                  <Route
                    exact
                    path="/external-courses"
                    component={ExternalCourses}
                  />
                  <Route exact path="/requests" component={Requests} />
                  <Route exact path="/courses/:courseId/" component={Get} />
                  <Route
                    exact
                    path={PathRoutes.CERTIFICATE_GENERATE}
                    render={({ match }) => {
                      const { certificateId, courseId } = match.params
                      return (
                        <div
                          style={{
                            overflow: 'hidden',
                            height: '0px',
                          }}
                        >
                          <Generate
                            certificateId={certificateId}
                            courseId={courseId}
                          />
                        </div>
                      )
                    }}
                  />
                  {/* Si se necesita envolver un componente en otro, se debe usar un wrapper */}
                  {/* TODO: Buscar una mejor forma de hacerlo. Menos lineas  */}
                  <Route
                    exact
                    path="/courses/:courseId/lessons"
                    component={LessonView}
                  />

                  <Route path={ADMIN_ROUTE} exact>
                    <AccessConsumer>
                      {({ loading, modules }) => {
                        if (loading) return <Loader overlay color />
                        if (modules.administration)
                          return <Redirect to={PathRoutes.COURSES} />
                        return <Redirect to={PathRoutes.MONITORING} />
                      }}
                    </AccessConsumer>
                  </Route>

                  {/* Admin COURSES*/}
                  <Route path={PathRoutes.COURSES}>
                    <AdminCoursesWrapper>
                      <Switch>
                        <Route exact path="/administration/courses/new">
                          <NewCourse />
                        </Route>
                        <Route path="/administration/courses/:courseId/edit">
                          <EditCourse />
                        </Route>
                        <Route
                          exact
                          path="/administration/courses/:courseId/monitoring/participant/:userId"
                        >
                          <UserMonitor />
                        </Route>
                        {/* Admin LESSONS */}
                        <Route
                          exact
                          path="/administration/courses/:courseId/lessons"
                        >
                          <LessonsIndex />
                        </Route>
                        <Route path="/administration/courses/:courseId/lessons/:lessonId/edit">
                          <BlockIndex />
                        </Route>
                        <Route
                          exact
                          path={PathRoutes.COURSE_PREVIEW}
                          component={CoursePreview}
                        />
                        <Route path={PathRoutes.TEMPLATES}>
                          <TemplatesAdministration />
                        </Route>
                        <Route path={PathRoutes.EXTERNAL_COURSE}>
                          <ExternalCourseAdministration />
                        </Route>
                        <Route path={PathRoutes.EXTERNAL_COURSES}>
                          <ExternalCoursesAdministration />
                        </Route>
                        <Route path={PathRoutes.COURSES}>
                          <CoursesAdministration />
                        </Route>
                      </Switch>
                    </AdminCoursesWrapper>
                  </Route>
                  <Route path={PathRoutes.MONITORING}>
                    <AdminCoursesWrapper>
                      <MainDataProvider>
                        <Monitoring />
                      </MainDataProvider>
                    </AdminCoursesWrapper>
                  </Route>
                  <Route exact path={PathRoutes.IMPORT_CENTER}>
                    <AdminCoursesWrapper>
                      <ImportCenter />
                    </AdminCoursesWrapper>
                  </Route>
                  {/* Evaluation */}
                  <Route
                    exact
                    path="/evaluation/:evaluationId/check"
                    render={({ match }) => (
                      <Evaluation
                        state={EvaluationStatesEnum.onCheck}
                        evaluationId={match.params.evaluationId}
                      />
                    )}
                  />
                  <Route
                    exact
                    path="/administration/evaluation/:evaluationId/preview"
                    render={({ match }) => (
                      <Evaluation
                        state={EvaluationStatesEnum.onPreview}
                        evaluationId={match.params.evaluationId}
                      />
                    )}
                  />
                  <Route path={PathRoutes.LEARNING_PATHS}>
                    <Provider store={newStore}>
                      <Switch>
                        <Route exact path={PathRoutes.LEARNING_PATHS}>
                          <LearningPaths />
                        </Route>
                        <Route path={PathRoutes.LEARNING_PATHS_CREATE}>
                          <LearningPathsManage />
                        </Route>
                        <Route path={PathRoutes.LEARNING_PATHS_UPDATE}>
                          <LearningPathsManage />
                        </Route>
                      </Switch>
                    </Provider>
                  </Route>
                  <Redirect
                    exact
                    from="/configuration/"
                    to="/configuration/users/"
                  />
                  <Route exact path="/configuration/user/:userId">
                    <User />
                  </Route>
                  <Route
                    exact
                    path="/configuration/user-profile/:userId"
                    render={(props) => (
                      <UserProfile userId={Number(props.match.params.userId)} />
                    )}
                  />
                  <Route exact path="/configuration/hierarchy/">
                    <Hierarchy />
                  </Route>
                  <Route path={ProfileRoutes.MAIN}>
                    <LmsProfilesPage />
                  </Route>
                  <Route exact path="/configuration/users/">
                    <Users />
                  </Route>
                  <Route exact path="/configuration/translations">
                    <TranslationsConfig />
                  </Route>
                  <Route exact path="/configuration/integrations">
                    <IntegrationsConfig />
                  </Route>
                  <Route
                    path="/view-all/:scope/:filter_by/:folder_id?/:folder_title?/:origin?"
                    component={ViewAll}
                  />
                  <Route path="/learning-path/:id" component={LearningPath} />
                  <>
                    <Provider store={newStore}>
                      <Route exact path="/webinars">
                        <Webinars />
                      </Route>
                    </Provider>
                  </>
                  <Route path="/forbidden" component={Error403} />
                  <Route path="/notFound" component={Error404} />
                  <Route component={Error404} />
                </Switch>
              </Layout>
            </AppRoutes>
          </Suspense>
        </ConnectedRouter>
      </Provider>
    </RankmiAmplitudeWrapper>
  </Contexts>,
  document.getElementById('root')
)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
